import { call, put, takeLatest } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchLMSTrainingState,
  CompleteLMSTrainingStep,
  UploadLMSCertificate,
  FetchDriver,
} from 'src/constants/actionTypes'
import { ApiService, callClientSecureApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import { LMSTrainingState } from 'src/interfaces/lmsTraining'
import { captureSentryError } from 'src/utils/sentry'
import { getApiErrorMessage } from 'src/utils/errorHandlers'
import { GcsFileReferenceDto, uploadFileToGcs } from './api/gcsUpload'

interface FetchLMSTrainingStateRequest {
  stepId: string
  lmsTrainingId: string
}

function* onFetchLMSTrainingState(
  action: AsyncAction<FetchLMSTrainingStateRequest, LMSTrainingState>,
) {
  const { lmsTrainingId, stepId } = action.payload

  try {
    const { payload }: { payload: LMSTrainingState } = yield call(
      callClientSecureApi,
      Endpoints.FetchLMSTrainingState,
      {
        apiService: ApiService.Paraworks,
        method: 'GET',
        query: {
          stepId,
          lmsTrainingId,
        },
      },
    )

    yield put(FetchLMSTrainingState.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.lmsTraining.onFetchLMSTrainingState] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to fetch LMS training info')

    yield put(FetchLMSTrainingState.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface CompleteLMSTrainingStepRequest {
  stepId: string
  skipCertificateValidation?: boolean
}

function* onCompleteLMSTrainingStep(
  action: AsyncAction<CompleteLMSTrainingStepRequest, LMSTrainingState>,
) {
  const { stepId, skipCertificateValidation } = action.payload

  const body: CompleteLMSTrainingStepRequest = { stepId }

  if (skipCertificateValidation) {
    body.skipCertificateValidation = true
  }

  try {
    const { payload }: { payload: LMSTrainingState } = yield call(
      callClientSecureApi,
      Endpoints.CompleteLMSTrainingStep,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body,
      },
    )

    yield put(CompleteLMSTrainingStep.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.lmsTraining.onCompleteLMSTrainingStep] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to complete LMS training step')

    yield put(CompleteLMSTrainingStep.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadLMSCertificateRequest {
  stepId: string
  file: File
}

function* onUploadLMSCertificate(action: AsyncAction<UploadLMSCertificateRequest>) {
  const { stepId, file } = action.payload

  try {
    const fileRef = (yield call(
      uploadFileToGcs,
      {
        stepId,
        originalname: file.name,
        mimeType: file.type,
        size: file.size,
      },
      file,
    )) as GcsFileReferenceDto

    yield call(callClientSecureApi, Endpoints.UploadLMSCertificate, {
      apiService: ApiService.Paraworks,
      method: 'POST',
      body: { stepId, file: fileRef },
    })

    yield put(UploadLMSCertificate.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.lmsTraining.onUploadLMSCertificate] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload LMS certificate')

    yield put(UploadLMSCertificate.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* lmsTrainingSaga() {
  yield takeLatest(FetchLMSTrainingState.type.REQUEST, onFetchLMSTrainingState)
  yield takeLatest(CompleteLMSTrainingStep.type.REQUEST, onCompleteLMSTrainingStep)
  yield takeLatest(UploadLMSCertificate.type.REQUEST, onUploadLMSCertificate)
}
